import React from 'react';
import { User, Truck, FileText, Search, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';

const SearchResults = ({ 
  results = [], 
  selectedIndex = -1, 
  isSearching = false, 
  onResultSelect, 
  query = '' 
}) => {
  const getStatusIcon = (result) => {
    if (result.type === 'driver') {
      const { hosStatus, licenseStatus } = result.data;
      if (hosStatus === 'Available') return <CheckCircle className="h-3 w-3 text-green-500" />;
      if (hosStatus === 'Driving') return <AlertTriangle className="h-3 w-3 text-yellow-500" />;
      if (licenseStatus === 'Expired') return <AlertTriangle className="h-3 w-3 text-red-500" />;
    }
    
    if (result.type === 'vehicle') {
      const { status } = result.data;
      if (status === 'active') return <CheckCircle className="h-3 w-3 text-green-500" />;
      if (status === 'maintenance') return <AlertTriangle className="h-3 w-3 text-yellow-500" />;
      if (status === 'out-of-service') return <AlertTriangle className="h-3 w-3 text-red-500" />;
    }
    
    return null;
  };

  const getResultTypeColor = (type) => {
    switch (type) {
      case 'driver': return 'text-blue-600 bg-blue-50';
      case 'vehicle': return 'text-green-600 bg-green-50';
      case 'document': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const highlightMatch = (text, searchQuery) => {
    if (!text || !searchQuery) return text;
    
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-gray-900 font-medium">
          {part}
        </mark>
      ) : part
    );
  };

  const getResultIcon = (result) => {
    const IconComponent = result.icon;
    return <IconComponent className="h-5 w-5 text-gray-500" />;
  };

  if (isSearching) {
    return (
      <div className="px-4 py-8 text-center">
        <Loader2 className="h-6 w-6 text-blue-500 mx-auto mb-2 animate-spin" />
        <p className="text-sm text-gray-600">Searching...</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No results found</p>
        <p className="text-gray-400 text-xs mt-1">
          Try adjusting your search terms or check spelling
        </p>
      </div>
    );
  }

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) acc[result.type] = [];
    acc[result.type].push(result);
    return acc;
  }, {});

  return (
    <div className="py-2">
      <div className="px-4 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Search Results
          </span>
          <span className="text-xs text-gray-500">
            {results.length} result{results.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      <div className="max-h-80 overflow-y-auto">
        {Object.entries(groupedResults).map(([type, typeResults]) => (
          <div key={type}>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center space-x-2">
                {type === 'driver' && <User className="h-4 w-4 text-gray-500" />}
                {type === 'vehicle' && <Truck className="h-4 w-4 text-gray-500" />}
                {type === 'document' && <FileText className="h-4 w-4 text-gray-500" />}
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {type}s ({typeResults.length})
                </span>
              </div>
            </div>
            
            {typeResults.map((result, index) => {
              const globalIndex = results.indexOf(result);
              return (
                <button
                  key={result.id}
                  onClick={() => onResultSelect(result)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors border-b border-gray-50 last:border-b-0 ${
                    selectedIndex === globalIndex ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getResultIcon(result)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {highlightMatch(result.title, query)}
                        </p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getResultTypeColor(result.type)}`}>
                          {result.type}
                        </span>
                        {getStatusIcon(result)}
                      </div>
                      
                      <p className="text-xs text-gray-500 line-clamp-2">
                        {highlightMatch(result.subtitle, query)}
                      </p>
                      
                      {/* Additional context based on type */}
                      {result.type === 'driver' && result.data.hosData && (
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-xs text-gray-400">
                            Driving: {Math.floor(result.data.hosData.drivingTimeToday / 60)}h {result.data.hosData.drivingTimeToday % 60}m
                          </span>
                          <span className="text-xs text-gray-400">
                            Remaining: {Math.floor(result.data.hosData.remainingDriving / 60)}h {result.data.hosData.remainingDriving % 60}m
                          </span>
                        </div>
                      )}
                      
                      {result.type === 'vehicle' && result.data.assignedDriver && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-400">
                            Assigned to: {result.data.assignedDriver}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      
      {results.length >= 50 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Showing first 50 results. Try refining your search for more specific results.
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;